import { getIntl, history } from "@umijs/max";
import { AppstoreOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { AdminShellActions, buildAdminAvatarProps, registerChunkLoadErrorReloadOnce } from "@airange/ui";
import { resolveUmiSsoGuardRedirect } from "@airange/ui/umi-sso-guard";
import { App as AntdApp, ConfigProvider } from "antd";
import type { ReactNode } from "react";
import "antd/dist/reset.css";
import { ApiError } from "@airange/shared/api";
import { logAirangeBuildInfo } from "@airange/shared/browser-build-info";
import { LOGIN_PATH, OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import defaultSettings from "@/app/default-settings";
import { LocaleSwitch } from "./components/LocaleSwitch";
import { PORTAL_BASE_URL } from "./lib/env";
import { initializeMicroLabAdminLocale } from "./lib/locale";
import { toMicroLabDisplayUser } from "./lib/micro-lab-auth-api";
import {
  buildMicroLabAdminDocumentTitle,
  resolveMicroLabAdminPageTitleId,
} from "./lib/page-title-rules";
import { buildMicroLabAdminBreadcrumbs } from "./lib/micro-lab-breadcrumbs";
import { resolveMicroLabAdminMenuSelectedKeys } from "./lib/menu-selection";
import { clearSession } from "./lib/session";
import { bootstrapSession, logout } from "./lib/sso";
import { toAppLocalPathname, toBrowserPath } from "./lib/public-path";
import { canAccessMicroLabTeacherWorkspace } from "./access";
import "./styles.css";

logAirangeBuildInfo(__AIRANGE_BUILD_INFO__);

const loginPath = LOGIN_PATH;

type LayoutMenuItem = {
  children?: LayoutMenuItem[];
  disabledTooltip?: boolean;
  [key: string]: unknown;
};

type PageTitleRenderProps = {
  location?: {
    pathname?: string;
  };
  pathname?: string;
  title?: string | false;
  pageName?: string;
};

type BreadcrumbCrumb = {
  path?: string;
  breadcrumbName?: string;
};

function disableMenuItemTooltips(items: LayoutMenuItem[] | undefined): LayoutMenuItem[] | undefined {
  if (!items) return items;
  return items.map((item) => ({
    ...item,
    disabledTooltip: true,
    children: disableMenuItemTooltips(item.children),
  }));
}

export async function getInitialState() {
  initializeMicroLabAdminLocale();
  const settings = defaultSettings as Partial<LayoutSettings>;
  const pathname = readCurrentPathname();
  if (pathname === OIDC_CALLBACK_PATH) {
    return { settings, authStatus: "no_session" as const, currentUser: null, microLabUser: null };
  }
  try {
    const currentUser = await bootstrapSession();
    if (!currentUser) {
      return { settings, authStatus: "no_session" as const, currentUser: null, microLabUser: null };
    }
    if (!canAccessMicroLabTeacherWorkspace(currentUser)) {
      return { settings, authStatus: "no_access" as const, currentUser, microLabUser: null };
    }
    return { settings, authStatus: "authenticated" as const, currentUser, microLabUser: currentUser };
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      return { settings, authStatus: "no_access" as const, currentUser: null, microLabUser: null };
    }
    throw error;
  }
}

function readCurrentPathname(): string {
  if (typeof window !== "undefined") {
    return toAppLocalPathname(window.location.pathname || "/");
  }
  return "/";
}

function readCurrentSearch(): string {
  if (typeof window !== "undefined") {
    return window.location.search || "";
  }
  return "";
}

function readCurrentHash(): string {
  if (typeof window !== "undefined") {
    return window.location.hash || "";
  }
  return "";
}

export const layout = ({ initialState }: { initialState?: Awaited<ReturnType<typeof getInitialState>> }) => {
  const currentUser = initialState?.currentUser ?? null;
  const microLabUser = initialState?.microLabUser ?? null;
  const displayUser = currentUser ? toMicroLabDisplayUser(currentUser) : null;
  const hasAccess = Boolean(currentUser && microLabUser) && canAccessMicroLabTeacherWorkspace(microLabUser);
  const displayName = displayUser?.full_name || displayUser?.username || "Micro Lab";
  const intl = getIntl();
  const centerTitle = intl.formatMessage({ id: "microLab.shell.title" });
  const siteTitle = intl.formatMessage({ id: "platform.admin.title" });
  const loginPageTitle = intl.formatMessage(
    { id: "microLab.shell.loginPageTitle" },
    { centerTitle },
  );

  return {
    ...initialState?.settings,
    title: centerTitle,
    menu: {
      locale: true,
    },
    menuProps: {
      selectedKeys: resolveMicroLabAdminMenuSelectedKeys(readCurrentPathname()),
    },
    postMenuData: (menuData: LayoutMenuItem[] | undefined) => disableMenuItemTooltips(menuData) ?? [],
    breadcrumbRender: () => {
      const pathname = readCurrentPathname();
      return buildMicroLabAdminBreadcrumbs(pathname, ({ id }) => intl.formatMessage({ id }));
    },
    itemRender: (route: BreadcrumbCrumb, _params: unknown, routes: BreadcrumbCrumb[] = []) => {
      const label = route.breadcrumbName;
      const isLast = routes.indexOf(route) === routes.length - 1;
      if (!label) {
        return null;
      }
      if (isLast || !route.path || route.path === "/") {
        return <span>{label}</span>;
      }
      const targetPath = route.path;
      return <span onClick={() => history.push(targetPath)}>{label}</span>;
    },
    pageTitleRender: (props: PageTitleRenderProps, defaultPageTitle?: string, info?: { pageName?: string }) => {
      const pathname = String(props?.location?.pathname || props?.pathname || history.location.pathname || "");
      const resolvedPageTitleId = resolveMicroLabAdminPageTitleId(pathname);
      const resolvedPageTitle = resolvedPageTitleId ? intl.formatMessage({ id: resolvedPageTitleId }) : null;
      return buildMicroLabAdminDocumentTitle({
        pathname,
        siteTitle,
        centerTitle,
        loginPageTitle,
        pageName: resolvedPageTitle || info?.pageName || props?.pageName,
        defaultPageTitle,
        routeTitle: props?.title,
      });
    },
    onPageChange: () => {
      const pathname = readCurrentPathname();
      if (pathname === OIDC_CALLBACK_PATH) {
        return;
      }
      const redirectTarget = resolveUmiSsoGuardRedirect({
        initialStateReady: Boolean(initialState),
        pathname,
        search: readCurrentSearch(),
        hash: readCurrentHash(),
        loginPath,
        hasAccess,
        defaultAuthorizedPath: "/schemes",
      });
      if (redirectTarget) {
        history.replace(redirectTarget);
        return;
      }
      if (pathname === "/") {
        history.replace("/schemes");
      }
    },
    actionsRender: () => AdminShellActions({
      localeNode: <LocaleSwitch />,
      links: [
        {
          key: "portal",
          label: intl.formatMessage({ id: "microLab.shell.portalEntry" }),
          href: PORTAL_BASE_URL,
          icon: <AppstoreOutlined />,
        },
      ],
    }),
    avatarProps: buildAdminAvatarProps({
      currentUser: displayUser,
      onLogout: async () => {
        try {
          await logout();
        } finally {
          clearSession();
          window.location.assign(toBrowserPath(`${loginPath}?logged_out=1`));
        }
      },
    }) || {
      title: displayName,
    },
    childrenRender: (children: ReactNode) => (
      <ConfigProvider theme={{ cssVar: { key: "micro-lab-admin" } }}>
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    ),
  };
};

registerChunkLoadErrorReloadOnce("micro-lab-admin");
